import type { Game, Prediction } from './types'

/**
 * Fills any unpredicted games in the gameweek with a fixed, uninformed 1-1 guess.
 * Deliberately NOT based on real team data — this is a safety net, not a competitive guess.
 * Marks each as isAutoPredicted so it can be visually flagged and excluded from
 * streaks / Banker eligibility.
 */
export function applyAutoPredict(
  games: Game[],
  predictions: Record<string, Prediction>
): { updatedPredictions: Record<string, Prediction>; autoPredictedGameIds: string[] } {
  const updatedPredictions = { ...predictions }
  const autoPredictedGameIds: string[] = []

  for (const game of games) {
    if (!updatedPredictions[game.id]) {
      updatedPredictions[game.id] = {
        gameId: game.id,
        homeScore: 1,
        awayScore: 1,
        isAutoPredicted: true,
      }
      autoPredictedGameIds.push(game.id)
    }
  }

  return { updatedPredictions, autoPredictedGameIds }
}

/**
 * Weighted toward realistic football scorelines (0-3 most likely, occasionally higher)
 * rather than a flat uniform range.
 */
function randomScore(): number {
  const weights = [15, 30, 27, 16, 8, 3, 1]
  const total = weights.reduce((sum, w) => sum + w, 0)
  let roll = Math.random() * total
  for (let score = 0; score < weights.length; score++) {
    roll -= weights[score]
    if (roll < 0) return score
  }
  return weights.length - 1
}

/**
 * Fills any unpredicted games in the gameweek with a random, plausible scoreline.
 * Used by the "simulate whole season" dev tool to produce a believable spread of
 * results across many gameweeks, unlike the fixed 1-1 fallback in applyAutoPredict.
 */
export function applyRandomPredict(
  games: Game[],
  predictions: Record<string, Prediction>
): { updatedPredictions: Record<string, Prediction>; autoPredictedGameIds: string[] } {
  const updatedPredictions = { ...predictions }
  const autoPredictedGameIds: string[] = []

  for (const game of games) {
    if (!updatedPredictions[game.id]) {
      updatedPredictions[game.id] = {
        gameId: game.id,
        homeScore: randomScore(),
        awayScore: randomScore(),
        isAutoPredicted: true,
      }
      autoPredictedGameIds.push(game.id)
    }
  }

  return { updatedPredictions, autoPredictedGameIds }
}